import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Mail, Building2, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  demo_unlock: { label: "Demo Unlock", color: "bg-emerald-100 text-emerald-700" },
  request_customization: { label: "Customization", color: "bg-amber-100 text-amber-700" },
  schedule_demo: { label: "Schedule Demo", color: "bg-blue-100 text-blue-700" },
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => {
    const params = filter ? `?request_type=${filter}` : "";
    api.get(`/admin/leads${params}`).then((r) => setLeads(r.data));
  };
  useEffect(load, [filter]);

  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-leads-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Leads & Requests</h1>
      <p className="text-slate-500 mt-1">Inbox of demo unlocks, customization requests, and demo bookings.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {["", "demo_unlock", "schedule_demo", "request_customization"].map((t) => (
          <button
            key={t || "all"}
            onClick={() => setFilter(t)}
            className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors ${
              filter === t ? "bg-slate-900 text-white" : "bg-white border border-slate-200 hover:border-slate-400 text-slate-600"
            }`}
            data-testid={`leads-filter-${t || "all"}`}
          >
            {t ? TYPE_LABEL[t].label : "All"}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {leads.map((l) => {
          const t = TYPE_LABEL[l.request_type] || { label: l.request_type, color: "bg-slate-100 text-slate-700" };
          return (
            <div key={l.id} className="p-5 rounded-2xl bg-white border border-slate-200" data-testid={`lead-row-${l.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${t.color} border-0`}>{t.label}</Badge>
                    {l.system_name && <span className="text-xs text-slate-500">→ {l.system_name}</span>}
                    <span className="text-xs text-slate-400 ml-auto">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 font-heading text-lg font-semibold">{l.name || l.email}</div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-600">
                    <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" /> {l.email}</div>
                    {l.company && <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-slate-400" /> {l.company}</div>}
                    {l.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-400" /> {l.phone}</div>}
                  </div>
                  {l.message && (
                    <div className="mt-3 text-sm text-slate-700 bg-slate-50 rounded-xl p-3 flex gap-2">
                      <MessageSquare className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                      <span className="leading-relaxed">{l.message}</span>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            </div>
          );
        })}
        {leads.length === 0 && (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl text-slate-500" data-testid="leads-empty">
            No leads yet.
          </div>
        )}
      </div>
    </div>
  );
}
